import React from "react";
import { PropTypes } from "prop-types";
import { observer } from "mobx-react";
import { MrbListPage, MrbPageHeader, MrbPageContentHeader } from "mrb/components/layout";
import { MrbTable } from "mrb/components/table";
import { MrbQuerySearchInput } from "mrb/components/input";
import { MrbButton } from "mrb/components/button";
import { MrbRouterLink } from "mrb/core/infrastructure/router";
import { TableNoDataOverlay } from "common/components";
import { defaultTemplate } from "common/hoc";

function deunderscore(value) {
    return value ? value.replace(/_/g, " ") : "—";
}

function formatBytes(bytes) {
    if (!bytes) return "—";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

function UploadedContentSessionTemplate({ uploadedContentSessionViewStore, t }) {
    const {
        gridStore,
        queryUtility,
        sessionMetadata,
        selectedItems,
        downloadSelected,
        downloadAll,
    } = uploadedContentSessionViewStore;

    return (
        <MrbListPage store={uploadedContentSessionViewStore} t={t}>
            <MrbPageHeader>
                <div className="c-page__header--top">
                    <MrbRouterLink routeName="master.administration.uploaded-content.list">
                        <span className="display--ib u-mar--right--tny align--v--middle">
                            <i className="u-icon u-icon--med u-icon--back" />
                        </span>
                        {t("ADMINISTRATION.UPLOADED_CONTENT.LIST.TITLE")}
                    </MrbRouterLink>
                    <div className="c-header__actions c-header__actions--end">
                        {selectedItems.length > 0 && (
                            <MrbButton
                                classNameOverride="c-btn c-btn--base c-btn--ghost u-mar--right--sml"
                                icon="u-icon u-icon--lrg u-icon--download--primary u-mar--right--tny"
                                label={t("ADMINISTRATION.UPLOADED_CONTENT.SESSION.DOWNLOAD_SELECTED", { count: selectedItems.length })}
                                onClick={downloadSelected}
                            />
                        )}
                        <MrbButton
                            classNameOverride="c-btn c-btn--base c-btn--primary"
                            icon="u-icon u-icon--lrg u-icon--download u-mar--right--tny"
                            label={t("ADMINISTRATION.UPLOADED_CONTENT.SESSION.DOWNLOAD_ALL")}
                            onClick={downloadAll}
                        />
                    </div>
                </div>
            </MrbPageHeader>
            <MrbPageContentHeader classNameOverride="u-mar--bottom--sml">
                <MrbQuerySearchInput queryUtility={queryUtility} />
            </MrbPageContentHeader>
            <div className="w-uploaded-content-session__files-layout u-mar--bottom--sml">
                <div className="w-uploaded-content-session__files-layout__table">
                    <MrbTable
                        t={t}
                        tableStore={gridStore}
                        className="ag-theme-alpine"
                        emptyStateRender={TableNoDataOverlay}
                    />
                </div>
                {sessionMetadata && (
                    <div className="c-card--primary w-uploaded-content-session__files-layout__metadata">
                        <SessionMetadataHeader metadata={sessionMetadata} t={t} />
                    </div>
                )}
            </div>
        </MrbListPage>
    );
}

const SessionMetadataHeader = observer(function SessionMetadataHeader({ metadata, t }) {
    return (
        <div className="u-display--flex u-display--flex--column u-gap--sml">
            <MetaField label={t("ADMINISTRATION.UPLOADED_CONTENT.SESSION.HEADER.PRODUCER")} value={deunderscore(metadata.producer)} />
            <MetaField label={t("ADMINISTRATION.UPLOADED_CONTENT.SESSION.HEADER.ARTIST")} value={deunderscore(metadata.artist)} />
            <MetaField label={t("ADMINISTRATION.UPLOADED_CONTENT.SESSION.HEADER.ALBUM")} value={deunderscore(metadata.album)} />
            {metadata.uploadedAt && (
                <MetaField
                    label={t("ADMINISTRATION.UPLOADED_CONTENT.SESSION.HEADER.UPLOAD_DATE")}
                    value={new Date(metadata.uploadedAt).toLocaleString()}
                />
            )}
            {metadata.fileCount != null && (
                <MetaField label={t("ADMINISTRATION.UPLOADED_CONTENT.SESSION.HEADER.FILE_COUNT")} value={metadata.fileCount} />
            )}
            {metadata.totalSize != null && (
                <MetaField label={t("ADMINISTRATION.UPLOADED_CONTENT.SESSION.HEADER.TOTAL_SIZE")} value={formatBytes(metadata.totalSize)} />
            )}
            {metadata.description && (
                <MetaField label={t("ADMINISTRATION.UPLOADED_CONTENT.SESSION.HEADER.DESCRIPTION")} value={metadata.description} />
            )}
        </div>
    );
});

function MetaField({ label, value }) {
    return (
        <div className="u-display--flex u-display--flex--column">
            <p className="u-type--sml u-type--color--opaque">{label}</p>
            <p className="u-type--base u-type--wgt--bold">{value}</p>
        </div>
    );
}

UploadedContentSessionTemplate.propTypes = {
    uploadedContentSessionViewStore: PropTypes.object.isRequired,
    t: PropTypes.func,
};

export default defaultTemplate(UploadedContentSessionTemplate);
